import * as React from 'react'
import PropTypes from 'prop-types'
import { midas } from '@resurs/midas'
import { Conditional } from '@resurs/react-utils'
import { TextInput } from '@resurs/midas-react'
import style from '../../shared/transactions.style'
import {
  calculateVatSum,
  FormattedAmount,
  FormattedUnits,
} from '../../shared/transaction-utils'
import { ActionTypeInputField } from './ActionTypeInputField'
import { getText } from '../../../../internal/TextLabels'
import { ActionTypes } from '../../../../util/transactionConstants'

export const TransactionCartUIEcomTableRow = ({
  article,
  index,
  sumOperations,
  cartArticles,
  action,
  checkAvailability,
  handleNewSumChange,
  handleNewQuantityChange,
  handleNewSumDescriptionChange,
  handleNewSumVatChange,
}) => {
  return (
    <tr
      disabled={
        action.type && sumOperations && index !== cartArticles.length - 1
      }
    >
      <td key="reference">{article.reference}</td>
      <td key="article-desc" style={{ minWidth: '10rem' }}>
        <Conditional.Group>
          <Conditional
            when={
              !sumOperations ||
              action.type === null ||
              index !== cartArticles.length - 1
            }
          >
            {article.description}
          </Conditional>
          <Conditional.NoMatch>
            <div
              style={{
                minWidth: '10rem',
                marginTop: '-1rem',
              }}
            >
              <TextInput
                type="normal"
                id="newSumDescription"
                name="newSumDescription"
                placeholder={article.newSumDescription}
                value={
                  article.newSumDescription ===
                  getText('transactions.transaction.sumAction.unspecified')
                    ? ''
                    : article.newSumDescription
                }
                onChange={e => {
                  handleNewSumDescriptionChange(e, index)
                }}
                autoFocus
                skipValidation
              />
            </div>
          </Conditional.NoMatch>
        </Conditional.Group>
      </td>
      <td key="unit-price-vat" className={midas.textRight}>
        {FormattedAmount(article.unitPriceIncVat) ||
          FormattedAmount(+article.newSum) ||
          FormattedAmount(0)}
      </td>
      <td key="vat-rate" className={midas.textCenter}>
        <Conditional.Group>
          <Conditional
            when={
              sumOperations &&
              action.type !== null &&
              index === cartArticles.length - 1
            }
          >
            <ActionTypeInputField
              id="newSumVat"
              name="newSumVat"
              index={index}
              value={`${Number(article.vatRate * 100).toFixed(2)}`}
              suffix="%"
              validationClassName={
                article.vatRate > 1 ? midas.hasDanger : midas.hasSuccess
              }
              onChange={value => {
                handleNewSumVatChange(Math.min(value, 100), index)
              }}
              disabled={false}
              skipValidation
            />
          </Conditional>
          <Conditional.NoMatch>
            {`${Number(article.vatRate * 100).toFixed(2)} %`}
          </Conditional.NoMatch>
        </Conditional.Group>
      </td>
      <td key="total-inc-vat" className={midas.textRight}>
        {FormattedAmount(article.totalAmountIncVat) ||
          FormattedAmount(+article.newSum) ||
          FormattedAmount(0)}
      </td>
      <td key="price-total" className={midas.textRight}>
        {FormattedAmount(
          article.totalAmountIncVat - article.totalAmountExVat,
        ) ||
          FormattedAmount(calculateVatSum(article.newSum, article.vatRate)) ||
          FormattedAmount(0)}
      </td>
      <td key="reserved-quantity" style={style.tableNumber}>
        {FormattedUnits(article.reservedQuantity, article.unit) !== 0
          ? FormattedUnits(article.reservedQuantity, article.unit)
          : ''}
      </td>
      <td
        key="debited"
        style={{
          borderLeft: '2rem solid white',
          textAlign: 'center',
          marginRight: 10,
        }}
      >
        <Conditional when={action.type === ActionTypes.DEBIT}>
          <Conditional
            when={checkAvailability(index, action.type) && !sumOperations}
          >
            <ActionTypeInputField
              id="newQuantity"
              validationClassName={
                !article.newQuantityValid ? midas.hasDanger : midas.hasSuccess
              }
              value={article.newQuantity}
              onChange={handleNewQuantityChange}
              index={index}
              suffix={article.unit}
              disabled={!checkAvailability(index, action.type)}
              allowNegative={article.reservedQuantity < 0}
            />
          </Conditional>
          <Conditional
            when={sumOperations && index === cartArticles.length - 1}
          >
            <ActionTypeInputField
              id="newSum"
              validationClassName={
                !article.newSumValid ? midas.hasDanger : midas.hasSuccess
              }
              value={article.newSum}
              onChange={handleNewSumChange}
              index={index}
              suffix={getText('default.currency')}
              disabled={false}
            />
          </Conditional>
          <Conditional when={sumOperations}>
            <div>{FormattedUnits(article.debitedQuantity, article.unit)}</div>
          </Conditional>
        </Conditional>
        <Conditional
          when={
            action.type !== ActionTypes.DEBIT ||
            (!sumOperations && !checkAvailability(index, action.type))
          }
        >
          <div>{FormattedUnits(article.debitedQuantity, article.unit)}</div>
        </Conditional>
      </td>
      <td
        key="credited"
        style={{
          borderLeft: '1rem solid white',
          textAlign: 'center',
        }}
      >
        <Conditional when={action.type === ActionTypes.CREDIT}>
          <Conditional
            when={checkAvailability(index, action.type) && !sumOperations}
          >
            <ActionTypeInputField
              id="newQuantity"
              key="newQuantity"
              validationClassName={
                !article.newQuantityValid ? midas.hasDanger : midas.hasSuccess
              }
              value={article.newQuantity}
              onChange={handleNewQuantityChange}
              index={index}
              suffix={article.unit}
              disabled={!checkAvailability(index, action.type)}
              allowNegative={article.reservedQuantity < 0}
            />
          </Conditional>
          <Conditional
            when={sumOperations && index === cartArticles.length - 1}
          >
            <ActionTypeInputField
              id="newSum"
              validationClassName={
                !article.newSumValid ? midas.hasDanger : midas.hasSuccess
              }
              value={article.newSum}
              onChange={handleNewSumChange}
              index={index}
              suffix={getText('default.currency')}
              disabled={false}
            />
          </Conditional>
          <Conditional when={sumOperations}>
            <div>{FormattedUnits(article.creditedQuantity, article.unit)}</div>
          </Conditional>
        </Conditional>
        <Conditional
          when={
            action.type !== ActionTypes.CREDIT ||
            (!sumOperations && !checkAvailability(index, action.type))
          }
        >
          <div>{FormattedUnits(article.creditedQuantity, article.unit)}</div>
        </Conditional>
      </td>
      <td
        key="annulled"
        style={{
          borderLeft: '1rem solid white',
          textAlign: 'center',
        }}
      >
        {checkAvailability(index, action.type) &&
        action.type === ActionTypes.ANNUL ? (
          <ActionTypeInputField
            id="newQuantity"
            key="newQuantity"
            validationClassName={
              !article.newQuantityValid ? midas.hasDanger : midas.hasSuccess
            }
            value={article.newQuantity}
            onChange={handleNewQuantityChange}
            index={index}
            suffix={article.unit}
            disabled={!checkAvailability(index, action.type)}
            allowNegative={article.reservedQuantity < 0}
          />
        ) : (
          <div>{FormattedUnits(article.canceledQuantity, article.unit)}</div>
        )}
      </td>
    </tr>
  )
}
TransactionCartUIEcomTableRow.propTypes = {
  article: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  sumOperations: PropTypes.bool.isRequired,
  cartArticles: PropTypes.array.isRequired,
  action: PropTypes.object.isRequired,
  checkAvailability: PropTypes.func.isRequired,
  handleNewSumChange: PropTypes.func.isRequired,
  handleNewQuantityChange: PropTypes.func.isRequired,
  handleNewSumDescriptionChange: PropTypes.func.isRequired,
  handleNewSumVatChange: PropTypes.func.isRequired,
}
